import { useParams } from "react-router-dom";
import useAxios from "../../../hooks/useAxios";
import { useEffect, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { generateArray } from "../../../util/Utilities";
import DateFormatter from "../../../components/DateFormatter";
import UpdatePlanAccount from "../../../components/UpdatePlanAccount";
import UpdatePlanStaff from "../../../components/UpdatePlanStaff";
import { Button, Modal, Spinner } from "react-bootstrap";
import UpdatePlanUf from "../../../components/UpdatePlanUf";
import clsx from "clsx";
import UserHavePermission from "../../../util/UserHavePermission";
import { mainPermissions } from "../../../util/MenuLinks";
import ImportPlanAccountExpense from "../../../components/ImportPlanAccountExpense";
import { FaInfoCircle, FaUsers } from "react-icons/fa";
import { MdModeEdit, MdSave } from "react-icons/md";
import { FiSearch, FiCalendar, FiClock, FiLock, FiUnlock, FiCheckCircle, FiRefreshCw, FiBookmark } from "react-icons/fi";
import { BsArrowLeft, BsTrash } from "react-icons/bs";
import swal from "sweetalert";
import SavePlanVersionModal from "../../../components/Plans/SavePlanVersionModal";
import PlanVersionsModal from "../../../components/Plans/PlanVersionsModal";
import ClosePlanSurveyModal from "../../../components/Plans/ClosePlanSurveyModal";
import "./PlansManagement2.css";

const PlansManagement2 = () => {
    const { id } = useParams();
    const { background } = useTheme();

    const [filters, setFilters] = useState({
        code: '',
        accountClasification: '',
        name: ''
    });

    const [modeEdit, setModeEdit] = useState(false);
    const [compactView, setCompactView] = useState(true);
    const [description, setDescription] = useState('');
    const [showResponsibles, setShowResponsibles] = useState(false);
    const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
    const [showVersionsModal, setShowVersionsModal] = useState(false);
    const [showCloseSurveyModal, setShowCloseSurveyModal] = useState(false);
    const [accountsToMap, setAccountsToMap] = useState([]);

    const [{ data: versionsData }, getVersions] = useAxios(
        { url: `/plans/${id}/versions` },
        { useCache: false }
    );

    const [{ data, loading }, getAccount] = useAxios(
        { url: `/plans/${id}/accounts/render`, params: filters },
        { useCache: false }
    );

    const [{ data: temporaryAccounts, loading: loadingDataForEdit }, getAccountsForEdit] = useAxios(
        { url: `/temporary-planning-accounts/${id}` },
        { manual: true, useCache: false }
    );

    const [{ loading: loadingRemoveAccounts }, removeAccounts] = useAxios(
        { url: `/temporary-planning-accounts/${id}/cancel`, method: 'DELETE' },
        { manual: true, useCache: false }
    );

    const [{ loading: loadingApplyAccounts }, applyAccounts] = useAxios(
        { url: `/temporary-planning-accounts/${id}/apply`, method: 'POST' },
        { manual: true, useCache: false }
    );

    const [{ loading: updatePlanLoading }, updatePlan] = useAxios(
        { method: 'PUT' },
        { useCache: false, manual: true }
    );



    useEffect(() => {
        if (modeEdit) {
            getAccountsForEdit({ params: filters });
        }
    }, [modeEdit, filters]);

    useEffect(() => {
        if (modeEdit) {
            if (temporaryAccounts) {
                setAccountsToMap(temporaryAccounts);
            }
        } else {
            if (data) {
                setAccountsToMap(data?.accounts);
            }
        }
    }, [data, modeEdit, temporaryAccounts]);

    const handleModeEdit = () => {
        getAccountsForEdit().then(() => {
            setModeEdit(true);
        });
    };

    const handleRefresh = () => {
        setAccountsToMap([]);
        setTimeout(() => {
            setAccountsToMap(data?.accounts);
            setModeEdit(false);
        }, 100);
    };

    const handleRemoveAccounts = () => {
        removeAccounts().then(() => {
            handleRefresh();
        });
    };

    const handleUpdate = (action) => {
        if (action === 'closed') {
            if (data?.closed_at) {
                // Si el plan ya está cerrado, permitir reabrirlo directamente
                updatePlan({ url: `/plans/${id}/toggle-status` }).then(() => getAccount());
            } else {
                // Si el plan está abierto, exigir completar la encuesta de satisfacción
                setShowCloseSurveyModal(true);
            }
        }
        if (action === 'approved') updatePlan({ url: `/plans/${id}/approve` }).then(() => getAccount());
    };

    const handleBack = () => {
        handleRefresh();
    };

    const handleSaveUpdate = () => {
        swal({
            title: "¿Estás Seguro(a)?",
            text: "Esto sobreescribirá los valores de la planificación actual",
            icon: "warning",
            buttons: ["Cancelar", "Sí, aplicar cambios"],
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                applyAccounts?.().then(() => {
                    getAccount().then(() => {
                        handleRefresh();
                    });
                });
            }
        });
    };

    const handleFilters = (e) => {
        setFilters((oldFilters) => ({
            ...oldFilters,
            [e.target.name]: e.target.value
        }));
    };

    const handleRefreshTotals = () => {
        getVersions();
        if (!modeEdit) {
            getAccount({ params: filters }).then((response) => {
                if (response?.data?.accounts) {
                    setAccountsToMap(response.data.accounts);
                }
            });
        } else {
            getAccountsForEdit({ params: filters }).then((response) => {
                if (response?.data) {
                    setAccountsToMap(response.data);
                }
            });
        }
    };

    const isDark = background?.value === 'dark';

    return (
        <div className="pb-1">
            {/* Header Card */}
            <div className="pm-header-card">
                <div className="d-flex align-items-center justify-content-between gap-2 flex-nowrap pm-header-row">
                    {/* Left: Identity & Details */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {data?.cost_center_code && (
                            <span className="pm-badge-code">
                                {data?.cost_center_code}
                            </span>
                        )}
                        <span className="pm-title-text" title={data?.cost_center_name}>
                            {data?.cost_center_name || 'Cargando centro de costo...'}
                        </span>
                        <span className="badge bg-light text-primary border pm-year-badge" title="Año de planificación">
                            {data?.year}
                        </span>
                        {(data?.start || data?.end) && (
                            <span className="pm-meta-item text-muted d-none d-xxl-inline" title={`Disponible del ${data?.start} al ${data?.end}`}>
                                <FiClock /> {data?.start} al {data?.end}
                            </span>
                        )}
                    </div>

                    {/* Right: Status Pills & Action Toolbar */}
                    <div className="d-flex align-items-center gap-1 ms-auto flex-nowrap flex-shrink-0">
                        {/* Status: Cierre */}
                        <div
                            className={clsx("pm-status-pill", data?.closed_at ? "is-danger" : "is-success")}
                            title={data?.closed_at ? `Cerrado el ${data?.closed_at}` : 'Abierto (Sin fecha de cierre)'}
                        >
                            {data?.closed_at ? (
                                <FiLock className="text-danger" style={{ fontSize: 13 }} />
                            ) : (
                                <FiUnlock className="text-success" style={{ fontSize: 13 }} />
                            )}
                            <span className="fw-semibold" style={{ fontSize: '0.74rem' }}>
                                {data?.closed_at ? 'Cerrado' : 'Abierto'}
                            </span>
                            {data?.permissions?.plan?.close && (
                                <button
                                    onClick={() => handleUpdate('closed')}
                                    disabled={updatePlanLoading}
                                    className={clsx("btn btn-xs ms-1", data?.closed_at ? "btn-outline-success" : "btn-outline-danger")}
                                    style={{ fontSize: '0.68rem', padding: '0.1rem 0.35rem', lineHeight: 1.1 }}
                                >
                                    {data?.closed_at ? 'Abrir' : 'Cerrar'}
                                </button>
                            )}
                        </div>

                        {/* Status: Aprobación */}
                        <div
                            className={clsx("pm-status-pill", data?.approved_at ? "is-success" : "is-warning")}
                            title={data?.approved_at ? `Aprobado el ${data?.approved_at}` : 'Pendiente de aprobación'}
                        >
                            {data?.approved_at ? (
                                <FiCheckCircle className="text-success" style={{ fontSize: 13 }} />
                            ) : (
                                <FiClock className="text-warning" style={{ fontSize: 13 }} />
                            )}
                            <span className="fw-semibold" style={{ fontSize: '0.74rem' }}>
                                {data?.approved_at ? 'Aprobado' : 'Sin aprobar'}
                            </span>
                            {data?.permissions?.plan?.approve && (
                                <button
                                    onClick={() => handleUpdate('approved')}
                                    disabled={updatePlanLoading}
                                    className={clsx("btn btn-xs ms-1", data?.approved_at ? "btn-outline-danger" : "btn-outline-warning")}
                                    style={{ fontSize: '0.68rem', padding: '0.1rem 0.35rem', lineHeight: 1.1 }}
                                >
                                    {data?.approved_at ? 'Rechazar' : 'Aprobar'}
                                </button>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="d-flex align-items-center gap-1 border-start ps-1 ms-1">
                            {UserHavePermission(mainPermissions?.planAccounts?.[0]) && (
                                <ImportPlanAccountExpense planId={id} />
                            )}

                            <button
                                className="btn btn-outline-secondary btn-xs d-inline-flex align-items-center gap-1"
                                onClick={() => setShowResponsibles(true)}
                                style={{ height: 28, fontSize: '0.75rem', padding: '0 8px' }}
                                title="Ver responsables asignados"
                            >
                                <FaUsers />
                                <span>Planificadores</span>
                            </button>

                            <button
                                className="btn btn-outline-primary btn-xs d-inline-flex align-items-center gap-1"
                                onClick={handleRefreshTotals}
                                disabled={loading}
                                style={{ height: 28, fontSize: '0.75rem', padding: '0 8px' }}
                                title="Recargar y actualizar totales"
                            >
                                <FiRefreshCw className={clsx({ "fa-spin": loading })} />
                                <span>Actualizar</span>
                            </button>

                            {/* Guardar Versión */}
                            {!modeEdit && (
                                <button
                                    className="btn btn-outline-success btn-xs d-inline-flex align-items-center gap-1"
                                    onClick={() => setShowSaveVersionModal(true)}
                                    style={{ height: 28, fontSize: '0.75rem', padding: '0 8px' }}
                                    title="Guardar un respaldo de la planificación actual"
                                >
                                    <FiBookmark />
                                    <span>Guardar Versión</span>
                                </button>
                            )}

                            {/* Historial de Versiones */}
                            <button
                                className="btn btn-outline-secondary btn-xs d-inline-flex align-items-center gap-1"
                                onClick={() => setShowVersionsModal(true)}
                                style={{ height: 28, fontSize: '0.75rem', padding: '0 8px' }}
                                title="Ver historial de versiones y respaldos guardados"
                            >
                                <FiClock />
                                <span>Versiones</span>
                                {versionsData && versionsData.length > 0 && (
                                    <span className="badge bg-primary text-white ms-1" style={{ fontSize: '0.65rem', padding: '2px 5px', borderRadius: 4 }}>
                                        {versionsData.length}
                                    </span>
                                )}
                            </button>

                            {/* Mode Edit Toggle Button */}
                            {UserHavePermission(mainPermissions?.temporaryPlanningAccount[0]) && !modeEdit && (
                                <button
                                    className="btn btn-primary btn-xs d-inline-flex align-items-center gap-1"
                                    onClick={handleModeEdit}
                                    disabled={loadingDataForEdit}
                                    style={{ height: 28, fontSize: '0.75rem', padding: '0 8px' }}
                                >
                                    {loadingDataForEdit ? (
                                        <Spinner size="sm" animation="border" />
                                    ) : (
                                        <>
                                            <MdModeEdit />
                                            <span>Modo Edición</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Mode Active Banner */}
            {modeEdit && (
                <div className="pm-edit-banner">
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-warning text-dark fw-bold" style={{ fontSize: '0.75rem' }}>
                            MODO EDICIÓN ACTIVO
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                            Estás editando en borrador temporal. Aplica los cambios para sobrescribir la planificación oficial.
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-secondary btn-xs d-inline-flex align-items-center gap-1"
                            onClick={handleBack}
                            style={{ height: 28 }}
                        >
                            <BsArrowLeft />
                            <span>Volver</span>
                        </button>

                        <button
                            className="btn btn-outline-danger btn-xs d-inline-flex align-items-center gap-1"
                            onClick={handleRemoveAccounts}
                            disabled={loadingRemoveAccounts}
                            style={{ height: 28 }}
                        >
                            {loadingRemoveAccounts ? (
                                <Spinner size="sm" animation="border" />
                            ) : (
                                <>
                                    <BsTrash />
                                    <span>Descartar cambios</span>
                                </>
                            )}
                        </button>

                        <button
                            className="btn btn-success btn-xs d-inline-flex align-items-center gap-1"
                            onClick={handleSaveUpdate}
                            disabled={loadingApplyAccounts}
                            style={{ height: 28 }}
                        >
                            {loadingApplyAccounts ? (
                                <Spinner size="sm" animation="border" />
                            ) : (
                                <>
                                    <MdSave />
                                    <span>Aplicar cambios</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Table Toolbar */}
            <div className="pm-toolbar">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                        Mostrando <strong>{accountsToMap?.length || 0}</strong> cuentas
                    </span>
                    {loading && (
                        <Spinner variant="primary" style={{ width: 14, height: 14 }} animation="border" />
                    )}
                </div>

                {/* View toggle (Compact vs Detailed) */}
                <div className="pm-view-toggle" title="Alternar entre vista compacta (mayor densidad) y vista detallada">
                    <button
                        type="button"
                        className={clsx("pm-view-toggle-btn", { active: compactView })}
                        onClick={() => setCompactView(true)}
                    >
                        Vista Compacta
                    </button>
                    <button
                        type="button"
                        className={clsx("pm-view-toggle-btn", { active: !compactView })}
                        onClick={() => setCompactView(false)}
                    >
                        Vista Detallada
                    </button>
                </div>
            </div>

            {/* Spreadsheet Grid Container */}
            <div className="pm-table-wrapper">
                <div
                    className={clsx("pm-table-scroll", {
                        "with-edit-banner": modeEdit
                    })}
                >
                    <table className="pm-table">
                        {/* Headings & Search Filters */}
                        <thead className="pm-thead">
                            <tr>
                                {/* Sticky 1: Estructura Costos */}
                                <th className="sticky-col pm-sticky-col-1">
                                    <div>Estructura Costos CSI</div>
                                    <div className="pm-search-box">
                                        <input
                                            type="text"
                                            className="pm-search-input"
                                            onChange={handleFilters}
                                            value={filters?.accountClasification}
                                            name="accountClasification"
                                            placeholder="Filtrar..."
                                        />
                                        <FiSearch className="pm-search-icon" />
                                    </div>
                                </th>

                                {/* Sticky 2: Lib. Mayor */}
                                <th className="sticky-col pm-sticky-col-2">
                                    <div>Lib. Mayor</div>
                                    <div className="pm-search-box">
                                        <input
                                            type="text"
                                            className="pm-search-input"
                                            onChange={handleFilters}
                                            value={filters?.code}
                                            name="code"
                                            placeholder="Filtrar..."
                                        />
                                        <FiSearch className="pm-search-icon" />
                                    </div>
                                </th>

                                {/* Sticky 3: Nombre Cuenta */}
                                <th className="sticky-col pm-sticky-col-3">
                                    <div>Nombre Cuenta</div>
                                    <div className="pm-search-box">
                                        <input
                                            type="text"
                                            className="pm-search-input"
                                            onChange={handleFilters}
                                            value={filters?.name}
                                            name="name"
                                            placeholder="Filtrar..."
                                        />
                                        <FiSearch className="pm-search-icon" />
                                    </div>
                                </th>

                                {/* 12 Months */}
                                {generateArray(12, 1).map((month) => (
                                    <th
                                        key={`month-${month}`}
                                        className="text-center pm-month-cell"
                                    >
                                        <span style={{ textTransform: 'capitalize' }}>
                                            {DateFormatter({
                                                value: `2023-${month}-15 12:00:00`,
                                                dateFormat: 'LLLL'
                                            })}
                                        </span>
                                    </th>
                                ))}

                                {/* Total Year */}
                                <th className="text-center pm-month-cell">
                                    <span>Total Año</span>
                                </th>
                            </tr>
                        </thead>

                        {/* Dotación Rows */}
                        <tbody>
                            <tr className="pm-staff-row">
                                <td colSpan={3} className="pm-body-sticky-col pm-sticky-col-1" style={{ zIndex: 8 }}>
                                    <div className="pm-staff-label">
                                        <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '0.7rem' }}>Dotación</span>
                                        <span>Solicitada (Personal adicional)</span>
                                    </div>
                                </td>
                                {data?.staff?.map((currentStaff, i) => (
                                    <td key={`staff-amount-${currentStaff?.id || i}`} className="pm-month-cell">
                                        {i < 12 ? (
                                            <UpdatePlanStaff
                                                planStaffId={currentStaff?.id}
                                                defaultValue={currentStaff?.amount}
                                                name="amount"
                                                canUpdate={data?.permissions?.staff?.amount || false}
                                            />
                                        ) : (
                                            <div className="pm-total-year-cell">
                                                {Number(currentStaff?.amount || 0)?.toLocaleString()}
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>

                            <tr className="pm-staff-row">
                                <td colSpan={3} className="pm-body-sticky-col pm-sticky-col-1" style={{ zIndex: 8 }}>
                                    <div className="pm-staff-label">
                                        <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '0.7rem' }}>Dotación</span>
                                        <span>POA (Asignada)</span>
                                    </div>
                                </td>
                                {data?.staff?.map((currentStaff, i) => (
                                    <td key={`staff-assigned-${currentStaff?.id || i}`} className="pm-month-cell">
                                        {i < 12 ? (
                                            <UpdatePlanStaff
                                                planStaffId={currentStaff?.id}
                                                defaultValue={currentStaff?.assigned}
                                                name="assigned"
                                                canUpdate={data?.permissions?.staff?.assigned || false}
                                            />
                                        ) : (
                                            <div className="pm-total-year-cell">
                                                {Number(currentStaff?.assigned || 0)?.toLocaleString()}
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        </tbody>

                        {/* Account Rows */}
                        <tbody>
                            {accountsToMap?.map((account, i) => (
                                <tr key={`accounts-${account?.code || i}-${i}`}>
                                    {/* Estructura Costos */}
                                    <td className="pm-body-sticky-col pm-sticky-col-1 px-2">
                                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.76rem' }}>
                                            {account?.accountClasification || '--'}
                                        </span>
                                    </td>

                                    {/* Lib. Mayor */}
                                    <td className="pm-body-sticky-col pm-sticky-col-2 px-2">
                                        <span
                                            className="fw-bold"
                                            style={{
                                                fontSize: '0.76rem',
                                                fontFamily: 'monospace',
                                                color: isDark ? '#e2e8f0' : '#1e293b'
                                            }}
                                        >
                                            {account?.code || '--'}
                                        </span>
                                    </td>

                                    {/* Nombre Cuenta */}
                                    <td className="pm-body-sticky-col pm-sticky-col-3 px-2">
                                        <div
                                            className="d-flex align-items-center justify-content-between text-truncate"
                                            title="Click para ver descripción"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setDescription(account?.description)}
                                        >
                                            <span
                                                className="fw-semibold text-truncate me-1"
                                                style={{
                                                    fontSize: '0.76rem',
                                                    color: isDark ? '#f1f5f9' : '#0f172a'
                                                }}
                                            >
                                                {account?.name || '--'}
                                            </span>
                                            <FaInfoCircle className="text-primary flex-shrink-0" style={{ fontSize: 12, opacity: 0.7 }} />
                                        </div>
                                    </td>

                                    {/* 12 Months + Total Year */}
                                    {account?.amount_values?.map((amountValue, i2) => (
                                        <td key={`account-val-${account?.code || i}-${i2}`} className="pm-month-cell">
                                            {i2 < 12 ? (
                                                <UpdatePlanAccount
                                                    account={account}
                                                    accountId={account?.ids?.[i2]}
                                                    defaultValue={amountValue}
                                                    staff={account?.staff}
                                                    closedAt={data?.closed_at}
                                                    approveAt={data?.approved_at}
                                                    editMode={modeEdit}
                                                    compactView={compactView}
                                                    onRefresh={handleRefreshTotals}
                                                />
                                            ) : (
                                                /* 13th column is Total Año */
                                                <div className="pm-total-year-cell">
                                                    {Number(amountValue || 0)?.toLocaleString()}$
                                                </div>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>

                        {/* Footer Totals */}
                        <tbody>
                            {/* Total Mes / Gastos */}
                            <tr className="pm-footer-row-total">
                                <td colSpan={3} className="sticky-col pm-sticky-col-1" style={{ zIndex: 8, background: '#0f172a' }}>
                                    <div className="pm-footer-title">
                                        Total Gastos Mes:
                                    </div>
                                </td>
                                {data?.monthsTotalsExpenses?.map((monthTotal, i2) => (
                                    <td key={`total-expenses-${i2}`} className="text-end pm-month-cell">
                                        <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                                            {Number(monthTotal || 0)?.toLocaleString()}$
                                        </span>
                                    </td>
                                ))}
                            </tr>

                            {/* Ingresos UF */}
                            <tr className="pm-footer-row-neutral">
                                <td colSpan={3} className="pm-body-sticky-col pm-sticky-col-1" style={{ zIndex: 8 }}>
                                    <div className="pm-footer-title text-muted">
                                        Ingresos UF:
                                    </div>
                                </td>
                                {data?.financial_unit_accounts?.map((financialUnitAccount, i2) => (
                                    <td key={`financial-unit-${i2}`} className="pm-month-cell">
                                        {i2 < 12 ? (
                                            <UpdatePlanUf
                                                financialUnitAccountId={financialUnitAccount?.id}
                                                defaultValue={financialUnitAccount?.amount}
                                                closedAt={data?.closed_at}
                                                approveAt={data?.approved_at}
                                                canUpdate={data?.permissions?.plan_uf?.update}
                                            />
                                        ) : (
                                            <div className="pm-total-year-cell">
                                                {Number(financialUnitAccount?.amount || 0)?.toLocaleString()} UF
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>

                            {/* Gastos en UF */}
                            <tr className="pm-footer-row-neutral">
                                <td colSpan={3} className="pm-body-sticky-col pm-sticky-col-1" style={{ zIndex: 8 }}>
                                    <div className="pm-footer-title text-muted">
                                        Gastos en UF:
                                    </div>
                                </td>
                                {data?.ufExpense?.map((monthTotal, i2) => (
                                    <td key={`uf-expense-${i2}`} className="pm-month-cell">
                                        {i2 < 12 ? (
                                            <div className="text-end">
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                                    {Number(monthTotal || 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} UF
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="pm-total-year-cell">
                                                {Number(monthTotal || 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} UF
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>

                            {/* UF */}
                            <tr className="pm-footer-row-neutral">
                                <td colSpan={3} className="pm-body-sticky-col pm-sticky-col-1" style={{ zIndex: 8 }}>
                                    <div className="pm-footer-title text-muted">
                                        Valor UF:
                                    </div>
                                </td>
                                {data?.uf?.map((monthTotal, i2) => (
                                    <td key={`uf-val-${i2}`} className="pm-month-cell">
                                        {i2 < 12 ? (
                                            <div className="text-end">
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                                    ${Number(monthTotal || 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="pm-total-year-cell text-muted">
                                                --
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>

                            {/* Resultado */}
                            <tr>
                                <td colSpan={3} className="sticky-col pm-sticky-col-1" style={{ zIndex: 8, background: '#1e293b' }}>
                                    <div className="pm-footer-title text-white">
                                        Resultado:
                                    </div>
                                </td>
                                {data?.monthsTotals?.map((monthTotal, i2) => {
                                    const isPositive = Number(monthTotal) > 0;
                                    const isNegative = Number(monthTotal) < 0;
                                    return (
                                        <td
                                            key={`result-${i2}`}
                                            className={clsx("text-end pm-month-cell", {
                                                'pm-footer-row-result-pos': isPositive,
                                                'pm-footer-row-result-neg': isNegative,
                                                'bg-light': !isPositive && !isNegative,
                                            })}
                                        >
                                            <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700 }}>
                                                {Number(monthTotal || 0)?.toLocaleString()}$
                                            </span>
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal: Responsibles */}
            <Modal size="lg" show={showResponsibles} onHide={() => setShowResponsibles(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Responsables de la Planificación
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                            Responsable General
                        </h6>
                        <div className="d-flex align-items-center p-2 rounded border bg-light">
                            <img
                                src={data?.general_responsible?.image_path}
                                alt=""
                                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <div className="ms-3">
                                <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                                    {data?.general_responsible?.name || 'No asignado'}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    {data?.general_responsible?.document_number || '--'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                            Equipo de Planificación
                        </h6>
                        <div className="row g-2">
                            {data?.responsibles?.map((responsible, i) => (
                                <div key={`resp-${i}`} className="col-md-6">
                                    <div className="d-flex align-items-center p-2 rounded border bg-light h-100">
                                        <img
                                            src={responsible?.image_path}
                                            alt=""
                                            style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }}
                                        />
                                        <div className="ms-2 text-truncate">
                                            <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.8rem' }}>
                                                {responsible?.name}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                {responsible?.document_number || '--'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {(!data?.responsibles || data?.responsibles.length === 0) && (
                                <div className="col-12 text-muted fst-italic" style={{ fontSize: '0.8rem' }}>
                                    No hay planificadores adicionales asignados.
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowResponsibles(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: Account Description */}
            <Modal show={Boolean(description)} onHide={() => setDescription('')} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                        Descripción de la Cuenta
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-dark mb-0" style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                        {description || 'Sin descripción registrada.'}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setDescription('')}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: Guardar Versión de Planificación */}
            <SavePlanVersionModal
                show={showSaveVersionModal}
                onHide={() => setShowSaveVersionModal(false)}
                planId={id}
                currentCostCenterName={data?.cost_center_name}
                currentYear={data?.year}
                onSaved={() => {
                    getVersions();
                    handleRefreshTotals();
                }}
            />

            {/* Modal: Historial y Restauración de Versiones */}
            <PlanVersionsModal
                show={showVersionsModal}
                onHide={() => setShowVersionsModal(false)}
                planId={id}
                canEdit={!data?.closed_at && !data?.approved_at && !modeEdit}
                onOpenSaveModal={() => setShowSaveVersionModal(true)}
                onRestored={() => {
                    getVersions();
                    handleRefreshTotals();
                }}
            />

            {/* Modal: Encuesta de Satisfacción Obligatoria para Cerrar Plan */}
            <ClosePlanSurveyModal
                show={showCloseSurveyModal}
                onHide={() => setShowCloseSurveyModal(false)}
                planId={id}
                costCenterName={data?.costCenter?.name}
                onClosed={() => {
                    getAccount();
                }}
            />
        </div>
    );
};

export default PlansManagement2;